<template>
  <div
    class="message-text__wrap"
    :class="{
      'show--quoted': isQuotedContentPresent,
      'hide--quoted': !isQuotedContentPresent,
    }"
  >
    <div
      v-if="isDraftActivity"
      class="text-content flex items-center gap-2 flex-wrap"
    >
      <p>{{ formattedDraftMessage }}</p>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        size="small"
        class="!px-2 !py-0.5 text-xs rounded-lg"
        @click="viewDraft"
      >
        {{ $t('CONVERSATION.DRAFT_ACTIVITY.VIEW_DRAFT') }}
        <fluent-icon icon="eye-show" size="14" />
      </woot-button>
    </div>
    <div v-else-if="!isEmail" v-dompurify-html="message" class="text-content" />
    <div v-else class="email-content-frame">
      <iframe
        ref="emailIframe"
        sandbox="allow-same-origin allow-popups"
        :srcdoc="emailIframeContent"
        frameborder="0"
        scrolling="no"
        class="email-iframe"
        @load="adjustIframeHeight"
      />
    </div>
    <button
      v-if="showQuoteToggle"
      class="text-slate-300 dark:text-slate-300 cursor-pointer text-xs py-1"
      @click="toggleQuotedContent"
    >
      <span v-if="showQuotedContent" class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-up" size="16" />
        {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
      </span>
      <span v-else class="flex items-center gap-0.5">
        <fluent-icon icon="chevron-down" size="16" />
        {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
      </span>
    </button>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';

export default {
  props: {
    message: {
      type: String,
      default: '',
    },
    isEmail: {
      type: Boolean,
      default: true,
    },
    isOutgoing: {
      type: Boolean,
      default: false,
    },
    displayQuotedButton: {
      type: Boolean,
      default: false,
    },
    contentAttributes: {
      type: Object,
      default: () => ({}),
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      showQuotedContent: false,
    };
  },
  computed: {
    isQuotedContentPresent() {
      if (!this.isEmail) {
        return this.message.includes('<blockquote');
      }
      return this.showQuotedContent;
    },
    showQuoteToggle() {
      if (!this.isEmail) {
        return false;
      }
      return this.displayQuotedButton;
    },
    isDraftActivity() {
      return this.contentAttributes && this.contentAttributes.draft_id;
    },
    draftId() {
      return this.contentAttributes?.draft_id;
    },
    formattedDraftMessage() {
      if (!this.isDraftActivity) return '';

      const scheduledAt = this.contentAttributes?.draft_scheduled_at;
      if (!scheduledAt) return 'Draft scheduled';

      const date = new Date(scheduledAt);
      const formattedDate = date.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      return `Email Draft scheduled for ${formattedDate}`;
    },
    emailIframeContent() {
      const sanitized = DOMPurify.sanitize(this.message, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ['style', 'link', 'meta', 'head', 'body', 'html'],
        ADD_ATTR: [
          'target', 'style', 'class', 'id', 'align', 'valign', 'bgcolor',
          'border', 'cellpadding', 'cellspacing', 'width', 'height',
          'colspan', 'rowspan', 'dir', 'face', 'color', 'size',
        ],
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
      });

      const textColor = this.isOutgoing ? '#fff' : '#1b1b1b';
      const linkColor = this.isOutgoing ? '#93c5fd' : '#1a73e8';
      const quoteBorder = this.isOutgoing
        ? 'rgba(255,255,255,0.4)'
        : 'rgba(0,0,0,0.2)';

      const baseStyles = `
        <style>
          body, body * {
            color: ${textColor} !important;
            background: transparent !important;
          }
          body {
            margin: 0;
            padding: 4px 8px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            word-wrap: break-word;
            overflow-wrap: break-word;
            overflow-x: hidden;
          }
          img {
            display: inline-block;
            max-width: 100% !important;
            height: auto !important;
          }
          table {
            border-collapse: collapse;
            max-width: 100% !important;
          }
          a, a * { color: ${linkColor} !important; }
          blockquote.chatwoot-quoted-thread {
            margin: 0;
            padding: 0;
            border: none;
            ${this.showQuotedContent ? '' : 'display: none;'}
          }
          blockquote:not(.chatwoot-quoted-thread) {
            margin: 0;
            padding-left: 12px;
            border-left: 3px solid ${quoteBorder};
          }
        </style>
      `;

      if (sanitized.includes('<html') || sanitized.includes('<head')) {
        return sanitized.replace(/<head([^>]*)>/i, `<head$1>${baseStyles}`);
      }
      return `<!DOCTYPE html><html><head>${baseStyles}</head><body>${sanitized}</body></html>`;
    },
  },
  watch: {
    showQuotedContent() {
      this.$nextTick(() => {
        this.adjustIframeHeight();
      });
    },
  },
  methods: {
    adjustIframeHeight() {
      const iframe = this.$refs.emailIframe;
      if (!iframe) return;

      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          const height = doc.body.scrollHeight;
          iframe.style.height = `${height + 10}px`;
        }
      } catch {
        iframe.style.height = '300px';
      }
    },
    toggleQuotedContent() {
      this.showQuotedContent = !this.showQuotedContent;
    },
    viewDraft() {
      if (this.draftId && this.conversationId) {
        this.$router.push({
          name: 'scheduled_drafts',
          params: {
            accountId: this.$route.params.accountId,
            conversation_id: this.conversationId,
          },
          query: {
            draft_id: this.draftId,
            from_activity: 'true',
          },
        });
      }
    },
  },
};
</script>
<style lang="scss">
.text-content {
  overflow: auto;

  ul,
  ol {
    padding-left: var(--space-two);
  }
  table {
    margin: 0;
    border: 0;

    td {
      margin: 0;
      border: 0;
    }

    tr {
      border-bottom: 0 !important;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: var(--font-size-normal);
  }
  p {
    font-weight: 500;
  }
}

.email-content-frame {
  overflow: hidden;

  .email-iframe {
    width: 100%;
    min-height: 50px;
    border: none;
    display: block;
    overflow: hidden;
    background: transparent;
  }
}

.show--quoted {
  blockquote {
    @apply block;
  }
}

.hide--quoted {
  blockquote {
    @apply hidden;
  }
}
</style>
